import { Show, type JSX } from "solid-js";
import type { GatewayStatus, RuntimePanel } from "../../model";
import type { AsyncPanel } from "../../state/async";
import { formatBytes, formatNumber } from "../../utils/format";
import { Modal } from "../../components/Modal";
import { PanelState } from "../../components/PanelState";
import { StatusBadge } from "../../components/StatusBadge";

interface HealthPanelProps {
  panel: AsyncPanel<GatewayStatus>;
  onOpenDetails: () => void;
}

export function HealthPanel(props: HealthPanelProps): JSX.Element {
  const status = () => props.panel.state().data;
  const runtime = () => {
    const panel = status()?.operational?.runtime;
    return panel?.status === "available" ? panel.snapshot : null;
  };
  const database = () => runtime()?.execution.database ?? null;
  const decoder = () => runtime()?.execution.requestBodyDecoder ?? null;
  return (
    <section class="bento bento--health" aria-labelledby="health-heading">
      <header class="bento__header bento__header--compact">
        <div>
          <p class="eyebrow">Gateway Health</p>
          <h2 id="health-heading">运行状态</h2>
        </div>
        <div class="bento__actions">
          <Show when={status()}>
            {(current) => (
              <StatusBadge
                label={current().meter.degraded ? "Degraded" : "Healthy"}
                tone={current().meter.degraded ? "warning" : "good"}
              />
            )}
          </Show>
          <button type="button" class="button button--ghost" onClick={props.onOpenDetails}>
            技术详情
          </button>
        </div>
      </header>
      <Show
        when={status()}
        fallback={<div class="skeleton-block" aria-label="正在加载 Gateway 状态" />}
      >
        {(current) => (
          <div class="health-strip">
            <div><span>In-flight</span><strong>{formatOptionalNumber(current().operational?.http.inFlight)}</strong></div>
            <div><span>DB read queue</span><strong>{formatOptionalNumber(database()?.readQueueDepth)}</strong></div>
            <div><span>DB write queue</span><strong>{formatOptionalNumber(database()?.writeQueueDepth)}</strong></div>
            <div><span>Decode queue</span><strong>{formatOptionalNumber(decoder()?.queueDepth)}</strong></div>
            <div><span>Runtime tasks</span><strong>{formatOptionalNumber(runtime()?.execution.tasksActive)}</strong></div>
            <div><span>Meter pending</span><strong>{formatNumber(current().meter.pending)}</strong></div>
            <div>
              <span>Dropped</span>
              <strong>{formatNumber(current().meter.queueDropped + current().meter.hotDropped + current().meter.coldDropped)}</strong>
            </div>
          </div>
        )}
      </Show>
      <PanelState state={props.panel.state()} onRetry={() => void props.panel.refresh()} />
    </section>
  );
}

interface HealthDetailsModalProps {
  status: GatewayStatus | null;
  onClose: () => void;
}

export function HealthDetailsModal(props: HealthDetailsModalProps): JSX.Element {
  return (
    <Modal title="Gateway 技术详情" eyebrow="只读运行状态" onClose={props.onClose} size="wide">
      <Show when={props.status} fallback={<p>Gateway 状态尚未成功加载。</p>}>
        {(status) => (
          <div class="detail-grid">
            <section>
              <h3>HTTP</h3>
              <dl class="detail-list">
                <dt>Requests total</dt><dd>{formatOptionalNumber(status().operational?.http.requestsTotal)}</dd>
                <dt>In-flight</dt><dd>{formatOptionalNumber(status().operational?.http.inFlight)}</dd>
                <dt>Canceled</dt><dd>{formatOptionalNumber(status().operational?.http.canceled)}</dd>
                <dt>Response bytes</dt><dd>{formatOptionalBytes(status().operational?.http.responseBytes)}</dd>
              </dl>
            </section>
            <section>
              <h3>Admission</h3>
              <dl class="detail-list">
                <dt>Global in-flight</dt><dd>{formatOptionalNumber(status().operational?.admission.globalInFlight)}</dd>
                <dt>Active keys</dt><dd>{formatOptionalNumber(status().operational?.admission.activeKeys)}</dd>
                <dt>Key in-flight</dt><dd>{formatOptionalNumber(status().operational?.admission.keyInFlight)}</dd>
                <dt>Search in-flight</dt><dd>{formatOptionalNumber(status().operational?.admission.searchInFlight)}</dd>
              </dl>
            </section>
            <section>
              <h3>Rust Process</h3>
              <RuntimeDetails runtime={status().operational?.runtime ?? { status: "unavailable" }} />
            </section>
            <section>
              <h3>Execution Saturation</h3>
              <Show
                when={runtimeDatabase(status().operational?.runtime)}
                fallback={<p class="callout">Database Execution Lane 状态不可用。</p>}
              >
                {(database) => (
                  <dl class="detail-list">
                    <dt>Read lanes</dt><dd>{formatNumber(database().readLanes)}</dd>
                    <dt>Read active</dt><dd>{formatNumber(database().readActive)}</dd>
                    <dt>Read queue</dt><dd>{formatNumber(database().readQueueDepth)} / {formatNumber(database().readQueueCapacity)}</dd>
                    <dt>Write lanes</dt><dd>{formatNumber(database().writeLanes)}</dd>
                    <dt>Write active</dt><dd>{formatNumber(database().writeActive)}</dd>
                    <dt>Write queue</dt><dd>{formatNumber(database().writeQueueDepth)} / {formatNumber(database().writeQueueCapacity)}</dd>
                  </dl>
                )}
              </Show>
              <Show when={runtimeRequestBodyDecoder(status().operational?.runtime)}>
                {(decoder) => (
                  <dl class="detail-list">
                    <dt>Decode workers</dt><dd>{formatNumber(decoder().workers)}</dd>
                    <dt>Decode active</dt><dd>{formatNumber(decoder().active)}</dd>
                    <dt>Decode queue</dt><dd>{formatNumber(decoder().queueDepth)} / {formatNumber(decoder().queueCapacity)}</dd>
                  </dl>
                )}
              </Show>
            </section>
            <section>
              <h3>Request Meter</h3>
              <dl class="detail-list">
                <dt>Accepted</dt><dd>{formatNumber(status().meter.accepted)}</dd>
                <dt>Hot written</dt><dd>{formatNumber(status().meter.hotWritten)}</dd>
                <dt>Hot failures</dt><dd>{formatNumber(status().meter.hotWriteFailures)}</dd>
                <dt>Cold archived</dt><dd>{formatNumber(status().meter.coldArchived)}</dd>
                <dt>Cold available</dt><dd>{status().meter.coldAvailable ? "是" : "否"}</dd>
                <dt>Writer healthy</dt><dd>{status().meter.writerHealthy ? "是" : "否"}</dd>
              </dl>
            </section>
          </div>
        )}
      </Show>
    </Modal>
  );
}

function RuntimeDetails(props: { runtime: RuntimePanel }): JSX.Element {
  return (
    <Show when={props.runtime.status === "available" ? props.runtime.snapshot : null} fallback={
      <p class="callout callout--warning">{runtimeUnavailableMessage(props.runtime)}</p>
    }>
      {(runtime) => (
        <dl class="detail-list">
          <dt>Schema</dt><dd>v{runtime().schemaVersion} / {runtime().runtimeKind}</dd>
          <dt>Collection</dt><dd>{runtime().collectionStatus}</dd>
          <dt>Uptime</dt><dd>{formatNumber(runtime().process.uptimeMS)} ms</dd>
          <dt>Resident memory</dt><dd>{formatOptionalBytes(runtime().process.residentMemoryBytes)}</dd>
          <dt>Virtual memory</dt><dd>{formatOptionalBytes(runtime().process.virtualMemoryBytes)}</dd>
          <dt>CPU user / system</dt><dd>{formatOptionalNumber(runtime().process.cpuUserMS)} / {formatOptionalNumber(runtime().process.cpuSystemMS)} ms</dd>
          <dt>Threads</dt><dd>{formatOptionalNumber(runtime().process.threads)}</dd>
          <dt>Open FDs</dt><dd>{formatOptionalNumber(runtime().process.openFileDescriptors)}</dd>
          <dt>Tasks / I/O workers</dt><dd>{formatNumber(runtime().execution.tasksActive)} / {formatNumber(runtime().execution.ioWorkers)}</dd>
        </dl>
      )}
    </Show>
  );
}

function runtimeUnavailableMessage(runtime: RuntimePanel): string {
  if (runtime.status === "unsupported") {
    return `不支持 Runtime Snapshot schema ${runtime.schemaVersion ?? "未知"} (${runtime.runtimeKind ?? "未知"})。`;
  }
  if (runtime.status === "invalid") return "Runtime Snapshot v1 数据无效，其余 Dashboard 保持可用。";
  return "Runtime Snapshot 不可用。";
}

function runtimeDatabase(runtime: RuntimePanel | undefined) {
  return runtime?.status === "available" ? runtime.snapshot.execution.database : null;
}

function runtimeRequestBodyDecoder(runtime: RuntimePanel | undefined) {
  return runtime?.status === "available" ? runtime.snapshot.execution.requestBodyDecoder : null;
}

function formatOptionalNumber(value: number | null | undefined): string {
  return value === undefined || value === null ? "不可用" : formatNumber(value);
}

function formatOptionalBytes(value: number | null | undefined): string {
  return value === undefined || value === null ? "不可用" : formatBytes(value);
}
